// Every blunder and miss of the reader's, one record each with what the
// classifier (plugin `shared-mistakes`) needs: the positions around the
// move, the scores, the engine's best line before and its reply after, and
// the material each line wins. The review's own judgements decide which
// moves are listed, so every record is a row of the moments table and has a
// puzzle. Each record also says how the reader stood before the move (by
// the Winning setting) and what their clock showed then.
import { type Color, type MistakeFact, type MistakeStanding, type Phase } from '@chess/api-model';
import { type FactExtractor, type ScannedGame } from '../extractor.ts';
import { lostPieceOf } from '../lost-piece.ts';
import { type GameFacts } from '@chess/api-model';

const PHASES: readonly Phase[] = ['opening', 'middlegame', 'ending'];
const other = (c: Color): Color => (c === 'white' ? 'black' : 'white');

/** The reader's standing at a score, by the Winning setting: the same lines as `StageStanding`. */
export function mistakeStanding(pawns: number, winningPawns: number): MistakeStanding {
  return pawns >= winningPawns ? 'winning' : pawns <= -winningPawns ? 'losing' : 'level';
}

/** Seconds on the reader's clock before their move at `ply`: the clock after their last clocked move before it; undefined without one. */
export function clockBefore(game: ScannedGame, ply: number): number | undefined {
  for (let p = ply - 2; p >= 1; p -= 2) {
    const seconds = game.clockAfter(p);
    if (seconds !== undefined) return seconds;
  }
  return undefined;
}

export const mistakes: FactExtractor = {
  id: 'mistakes',
  extract(game: ScannedGame, facts: GameFacts): void {
    const ranges = PHASES.map((phase) => ({ phase, ...game.range(phase) }));
    const out: MistakeFact[] = [];
    game.moves.forEach((move, i) => {
      const ply = i + 1;
      if (!game.isReaders(ply)) return;
      const kind = game.judge.kind(ply, move, game.reader);
      if (!kind) return;
      const stage = ranges.find((r) => ply > r.fromPly && ply <= r.toPly)?.phase;
      const before = game.at(ply - 1);
      const after = game.at(ply);
      const scoreBefore = game.pawnsAt(ply - 1);
      const scoreAfter = game.pawnsAt(ply);
      if (!stage || !before || !after || scoreBefore === undefined || scoreAfter === undefined) return;
      const best = before.lines[0];
      const reply = after.lines[0];
      const clock = clockBefore(game, ply);
      out.push({
        ply,
        san: move.san,
        uci: move.uci,
        kind,
        stage,
        fenBefore: before.fen,
        fenAfter: after.fen,
        before: scoreBefore,
        after: scoreAfter,
        best: best ? { pv: best.pv, mate: best.score.mate } : undefined,
        reply: reply ? { pv: reply.pv, mate: reply.score.mate } : undefined,
        lost: kind === 'blunder' ? lostPieceOf(after.fen, reply, after.score, game.reader) : undefined,
        // The best line's gain is the opponent's loss: the reader to move, the opponent losing.
        missed: kind === 'miss' ? lostPieceOf(before.fen, best, best?.score, other(game.reader)) : undefined,
        standing: mistakeStanding(scoreBefore, game.rules.winningPawns),
        ...(clock === undefined ? {} : { clock }),
      });
    });
    facts.mistakes = out;
  },
};
